import type {ReactElement} from 'react';
import React, {useContext} from 'react';
import type {Component} from 'grapesjs';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {Panel, PanelHeader} from '@Components/panel';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EmailMakerBottomSheet} from '@EmailMakerReact/components/email-maker-bottom-sheet';
import {useCloseSheetOnCanvasInteraction} from '@EmailMakerReact/hooks/use-close-sheet-on-canvas-interaction';
import {resolveControlByFlatIndex} from '@EmailMakerReact/lib/email-maker-tile-meta-library';
import type {EmailMakerResolvedControlAtIndex} from '@EmailMakerReact/lib/email-maker-tile-meta-library.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {MobileControlPanelProps, MountedSheetProps} from './email-maker-mobile-control-sheet.types';
import styles from './email-maker-mobile-control-sheet.module.scss';

const PANEL_ID = 'email-maker-mobile-control-panel';

export function EmailMakerMobileControlSheet(): ReactElement | null {
  const {editor, store, state} = useContext(emailMakerContext);
  const modalData = useAppSelector((s) => s.modals.modalsHashmap[MODALS_IDS.EMAIL_MAKER_MOBILE_CONTROL_MODAL]);

  if (!modalData?.isOpen) {
    return null;
  }

  const {flatIndex, isFullHeight} = modalData.panelProps as MobileControlPanelProps;
  const selected = state.selectedComponent;

  if (!selected || !editor) {
    return null;
  }

  const entry = resolveControlByFlatIndex(editor, selected, store, flatIndex);

  if (!entry) {
    return null;
  }

  return <MountedSheet entry={entry} selected={selected} isFullHeight={isFullHeight} />;
}

function MountedSheet({entry, selected: selectedComponent, isFullHeight}: MountedSheetProps): ReactElement {
  const dispatch = useAppDispatch();
  const {editor} = useContext(emailMakerContext);
  useDefaultPanelClose(PANEL_ID);

  useCloseSheetOnCanvasInteraction(editor, MODALS_IDS.EMAIL_MAKER_MOBILE_CONTROL_MODAL, {skipSelectedCid: selectedComponent.cid});

  const onClose = (): void => {
    dispatch(closeModal(MODALS_IDS.EMAIL_MAKER_MOBILE_CONTROL_MODAL));
  };

  return (
    <EmailMakerBottomSheet modalId={MODALS_IDS.EMAIL_MAKER_MOBILE_CONTROL_MODAL} panelId={PANEL_ID} modalMaxHeight={isFullHeight ? '90vh' : '60vh'}>
      <Panel
        panelHeader={<PanelHeader title={window.i18next.t(entry.labelKey)} onClose={onClose} />}
        panelContent={<div className={styles.body}>{renderControl(entry, selectedComponent)}</div>}
      />
    </EmailMakerBottomSheet>
  );
}

function renderControl(entry: EmailMakerResolvedControlAtIndex, selected: Component | null): ReactElement | null {
  if (!selected) {
    return null;
  }
  const {control, target} = entry;
  return React.createElement(control, {component: target, hideSectionHeading: true});
}
